import { WORKLOAD, IS_LITE } from '../core/edition';
import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { CELL, FIXED_DT, GROUND, PRESETS, type PresetName } from '../core/config';
import { JobQueue, Lifecycle } from '../core/lifecycle';
import {
  components,
  isRoot,
  damage,
  occupiedBoxes,
  occupiedBoxesSteps,
  partition,
  cellMass,
  cellDistance,
  physicalCenter,
  evaluateStructure,
  evaluateStructureSteps,
  type StructuralAssessment,
  type Box,
  type Vec,
} from '../destruction/structure';
import { type Building, type Cell, type City, MATERIALS, key } from '../world/city';
import { buildingGeometry, buildingGeometrySteps } from '../render/geometry';
import { exteriorCellMask } from '../render/exterior-mask';
import { aggregateMass, aggregateMassSteps } from './mass';
import { occupiedColliderGroups, occupiedColliderDesc } from './occupied-colliders';
import { forContactPoints } from './contact-points';
import { DormantNavigation, occupiedBoundsSteps } from './navigation';
import { FootClearance, FootGeometryChanges, type FlatSupport, type FootCache } from './foot-clearance';
import { DynamicFootQueries } from './dynamic-foot-queries';
import { DamageInbox, type DamageRequest } from '../destruction/inbox';
import { CellRegions } from '../destruction/cell-regions';
import { ClusterBatches } from '../render/cluster-batches';
import type { StaticUpload } from '../render/static-clusters';
import { BuildingGeometryCache } from '../render/building-cache';
import { refineFragment } from '../destruction/material-fracture';
import type { EntityToken, EntityHit } from '../core/entities';
import { flatTerrainPatch } from '../world/terrain';

export interface Cluster {
  id: number;
  revision: number;
  building: Building;
  cells: Map<number, Cell>;
  origin: THREE.Vector3;
  body: RAPIER.RigidBody;
  colliders: RAPIER.Collider[];
  colliderKeys: string[];
  colliderGroups: number;
  collisionBoxes: Box[];
  occupiedBounds?: THREE.Box3;
  mesh: THREE.Mesh;
  geometryCache?: BuildingGeometryCache;
  anchored: boolean;
  previous: THREE.Vector3;
  previousQ: THREE.Quaternion;
  radius: number;
  lastImpact: number;
  speedBeforeStep: number;
  velocityBeforeStep: THREE.Vector3;
  angularBeforeStep: THREE.Vector3;
  settled: boolean;
  quietSince: number;
  supportOwner?: number;
  supportPoint?: Vec;
  renderSleeping: boolean;
  detail: boolean;
  stress?: StructuralAssessment;
}
export interface DormantCluster {
  id: number;
  revision?: number;
  building: Building;
  cells: Map<number, Cell>;
  anchored: boolean;
  position: Vec;
  rotation: { x: number; y: number; z: number; w: number };
  origin: Vec;
  settled?: boolean;
  supportOwner?: number;
  supportPoint?: Vec;
}
export interface ReconstructionProgress {
  epoch: number;
  phase: number;
  cells: number;
  totalCells: number;
  districts: { id: string; cells: number; total: number; phase: number }[];
  longJobs: { phase: number; building: number; kind: string; cells: number; ms: number }[];
  preparing?: { building: number; operation: string };
}
export interface BlastEvent {
  id: number;
  epoch: number;
  point: Vec;
  radius: number;
  energy: number;
  heat: number;
  multiplicity?: number;
  source: 'c4' | 'vehicle' | 'impact' | 'airstrike';
}
export interface Hit {
  anchor?: string;
  dormant?: DormantCluster;
  point: THREE.Vector3;
  normal: THREE.Vector3;
  cluster?: Cluster;
  collider?: RAPIER.Collider;
  entity?: EntityToken;
}
export interface Disturbance {
  point: Vec;
  radius: number;
  strength: number;
  kind: 'impact' | 'explosion' | 'collapse';
}
export class PhysicsCity {
  readonly cellRegions = new CellRegions();
  private assessments = new Map<
    number,
    {
      revision: number;
      work: Generator<void, StructuralAssessment>;
      started: number;
      repeat: boolean;
    }
  >();
  private stressQueued = false;
  assessmentSliceMs = 0;
  assessmentLatencyMs = 0;
  world!: RAPIER.World;
  events!: RAPIER.EventQueue;
  private contactHandles: number[] = [];
  clusters = new Map<number, Cluster>();
  dormant = new Map<number, DormantCluster>();
  city!: City;
  pinnedCells = new Set<number>();
  stressed = new Set<number>();
  structuralFailures = 0;
  damagedCells = 0;
  initialCells = 0;
  streamedIn = 0;
  streamedOut = 0;
  private stressClock = 0;
  private streamClock = 0;
  private eventId = 0;
  private topologyRevision = 0;
  private buildingIndex = new Map<number, Building>();
  private buildingOwners = new Map<number, Set<number>>();
  private parentedByBuilding = new Map<number, Set<number>>();
  private pendingParented = new Set<number>();
  private runtimeStructures = new Set<number>();
  private cutaways = new Map<number, number>();
  private glazed?: { buildingId: number; low: number; high: number };
  readonly damageInbox = new DamageInbox();
  private captureQueue: {
    epoch: number;
    owner: number;
    cells: Cell[];
    local: Vec;
    request: DamageRequest;
    cursor: number;
    queuedAt: number;
  }[] = [];
  captureCells = 0;
  readonly batches: ClusterBatches;
  private pendingActivate = new Set<number>();
  private pendingPhysicsActivate = new Set<number>();
  private collisionPinnedUntil = new Map<number, number>();
  private streamFocus = { x: 0, z: 0, radius: 180 };
  onBlast: (event: BlastEvent) => void = () => {};
  onContact: (body: RAPIER.RigidBody, point: Vec, energy: number) => void = () => {};
  onChanged: (buildingId: number) => void = () => {};
  entityRay: (origin: Vec, direction: Vec, length: number) => EntityHit | null = () => null;
  entityForBody: (body: RAPIER.RigidBody) => EntityToken | undefined = () => undefined;
  colliderOwner = new Map<number, number>();
  readonly renderedDormant = new Set<number>();
  cellOwner = new Map<number, number>();
  readonly cellObservers = new Set<(cell: Cell) => void>();
  readonly supportObservers = new Set<(buildingId: number) => void>();
  private beforeCellChange = (cell: Cell): void => {
    for (const observe of this.cellObservers) observe(cell);
  };
  jobs: JobQueue;
  nextId = 1;
  time = 0;
  movingRubble = false;
  private movingRubbleBounds = new THREE.Box3();
  dangerousRubbleAt(p: Vec): boolean {
    const b = this.movingRubbleBounds;
    return (
      this.movingRubble &&
      p.x + 0.3 >= b.min.x &&
      p.x - 0.3 <= b.max.x &&
      p.z + 0.3 >= b.min.z &&
      p.z - 0.3 <= b.max.z &&
      p.y + 1.5 >= b.min.y &&
      p.y <= b.max.y
    );
  }
  structureVersion = 0;
  private supportVersions = new Map<number, number>();
  private supportSerial = 0;
  private supportBase = 0;
  buildingSupportVersion(id: number | undefined): number {
    return id === undefined ? 0 : (this.supportVersions.get(id) ?? this.supportBase);
  }
  private supportChanged(id: number): void {
    this.supportVersions.set(id, ++this.supportSerial);
    for (const observe of this.supportObservers) observe(id);
  }
  removed = 0;
  structuralMs = 0;
  rebuildProfile = {
    colliders: 0,
    geometry: 0,
    mass: 0,
    batch: 0,
    assessment: 0,
    cells: 0,
    retained: 0,
    created: 0,
    removed: 0,
  };
  physicsMs = 0;
  profileEnabled = false;
  stepSequence = 0;
  contactEvents = 0;
  totalContactEvents = 0;
  stepTimings: Record<string, number>[] = [];
  secondaryHits = 0;
  preset: PresetName = 'high';
  fragmentScale = 1;
  detailDistance = 180;
  private detailCursor = 0;
  private settlementCursor?: IterableIterator<Cluster>;
  private parkingCursor?: IterableIterator<Cluster>;
  private dependents = new Map<number, Set<number>>();
  settledTransitions = 0;
  reactivations = 0;
  private dynamicStructuralColliders = 0;
  private dynamicStructuralCuboids = 0;
  private readonly dormantNavigation = new DormantNavigation();
  private footGeometry = new FootGeometryChanges();
  /** Local obstacle revision for short, grounded steering decisions. */
  footGeometryVersion(point: Vec): number {
    return this.footGeometry.version(point);
  }
  get localGeometryStamp(): number {
    return this.footGeometry.revision;
  }
  localGeometryVersion(point: Vec, radius: number): number {
    return this.footGeometry.version(point, radius);
  }
  private invalidateFoot(c: Cluster): void {
    if (!c.occupiedBounds) return;
    const p = c.body.translation(),
      q = c.body.rotation();
    // The radius includes height: a 500m tower used to invalidate otherwise
    // unchanged streets hundreds of metres away on every local fracture.
    // Transform the exact occupied box, so a tilted/fallen section still
    // invalidates its complete projected footprint, including its height.
    const world = c.occupiedBounds
      .clone()
      .applyMatrix4(
        new THREE.Matrix4().compose(
          new THREE.Vector3(p.x, p.y, p.z),
          new THREE.Quaternion(q.x, q.y, q.z, q.w),
          new THREE.Vector3(1, 1, 1),
        ),
      );
    this.footGeometry.bounds(world.min.x, world.min.z, world.max.x, world.max.z);
  }
  readonly dynamicFootQueries = new DynamicFootQueries(() => this.world);
  readonly footClearance: FootClearance = new FootClearance({
    version: (point) => this.footGeometry.version(point),
    revision: () => this.footGeometry.revision,
    support: (point) => this.flatSupport(point),
    staticClear: (point, move) => {
      const start = { x: point.x, y: point.y + 0.8415, z: point.z },
        rotation = { x: 0, y: 0, z: 0, w: 1 },
        probe = this.footClearance.probe;
      return (
        !this.world.castShape(
          start,
          rotation,
          move,
          probe,
          0.0001,
          1,
          false,
          RAPIER.QueryFilterFlags.ONLY_FIXED,
        ) &&
        !this.dormantNavigation.sweepShape(
          start,
          move,
          probe,
          rotation,
          { x: 0.24, y: 0.8385, z: 0.24 },
          0.0001,
        )
      );
    },
    dynamicSweep: (point, move) => this.dynamicFootQueries.sweep(point, move, this.footClearance.probe),
  });
  onRubble: (power: number, point?: Vec) => void = () => {};
  onDisturbance: (e: Disturbance) => void = () => {};
  onParticles: (point: Vec, color: number, amount: number, power: number) => void = () => {};
  constructor(
    readonly scene: THREE.Scene,
    readonly material: THREE.Material | THREE.Material[],
    readonly life: Lifecycle,
  ) {
    this.jobs = new JobQueue(life);
    this.batches = new ClusterBatches(material);
    this.scene.add(this.batches.root);
  }
  private pristineGeometry = new Map<Building, THREE.BufferGeometry>();
  resetTemplateBytes = 0;
  reconstruction?: ReconstructionProgress;
  reconstructionStageLimit = Infinity;
  private reconstructionLayouts = new Map<Building, { origin: THREE.Vector3; parts: Cell[][] }>();
  private restorationCommittedRevision = -1;
  reconstructionQueryCommits = 0;
  reconstructionQuerySkips = 0;
  /** Rapier refreshes its query broad phase during step(). During reconstruction
   * the new world has no running activity; commit once per rendered work slice,
   * before cameras/navigation can query newly restored collision shapes. */
  commitReconstruction(): void {
    if (this.life.phase !== 'rebuilding' || !this.reconstruction || this.reconstruction.phase < 0) return;
    // Preparing vertices/bounds yields without changing any collider. Do not
    // step the unchanged world on each such yield. Occupant setup can move
    // vehicles independently of structural revisions, so always commit it.
    if (this.reconstruction.phase < 4 && this.restorationCommittedRevision === this.topologyRevision) {
      this.reconstructionQuerySkips++;
      return;
    }
    this.world.step(this.events);
    this.restorationCommittedRevision = this.topologyRevision;
    this.reconstructionQueryCommits++;
  }
  load(city: City, wantedFocus?: Vec, radius = 150, restoredCity = false): void {
    for (const step of this.loadSteps(city, wantedFocus, radius, restoredCity)) void step;
  }
  /** Startup drains this loader. Rebuild/Skip advance its actual structure and
   * collision phases only through bounded rendered-frame slices. */
  *loadSteps(
    city: City,
    wantedFocus?: Vec,
    radius = 150,
    restoredCity = false,
    staged = false,
  ): Generator<void> {
    this.structureVersion++;
    this.supportVersions.clear();
    this.supportBase = ++this.supportSerial;
    if (!restoredCity || this.city !== city) this.reconstructionLayouts.clear();
    this.reconstruction = staged
      ? { epoch: this.life.epoch, phase: -1, cells: 0, totalCells: 0, districts: [], longJobs: [] }
      : undefined;
    yield* this.disposeSteps(restoredCity && this.city === city);
    this.city = city;
    this.world = new RAPIER.World({ x: 0, y: -16, z: 0 });
    this.restorationCommittedRevision = -1;
    this.reconstructionQueryCommits = this.reconstructionQuerySkips = 0;
    this.world.timestep = FIXED_DT;
    // More convergence per 60Hz step reduces persistent contact motion. Actual
    // post-C4 snapshot replays cost less overall at four than two iterations;
    // support/contact and integrated recovery checks still govern acceptance.
    this.world.integrationParameters.numSolverIterations = 4;
    this.world.profilerEnabled = this.profileEnabled;
    this.events = new RAPIER.EventQueue(true);
    this.nextId = 1;
    this.removed = 0;
    this.time = 0;
    this.movingRubble = false;
    this.footClearance.reset();
    this.dynamicFootQueries.reset();
    this.footGeometry.reset();
    this.secondaryHits = 0;
    this.contactEvents = this.totalContactEvents = 0;
    this.settledTransitions = this.reactivations = 0;
    this.stepTimings.length = 0;
    this.structuralFailures = 0;
    this.damagedCells = 0;
    this.stressClock = 0;
    this.streamClock = 0;
    this.streamedIn = 0;
    this.streamedOut = 0;
    this.initialCells = 0;
    this.eventId = 0;
    const ground = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(0, city.terrain ? 0 : GROUND - 0.5, 0),
    );
    const groundShape = city.terrain
      ? RAPIER.ColliderDesc.trimesh(
          city.terrain.vertices,
          city.terrain.indices,
          RAPIER.TriMeshFlags.FIX_INTERNAL_EDGES,
        )
      : RAPIER.ColliderDesc.cuboid(city.half + 8, 0.5, city.half + 8);
    this.world.createCollider(groundShape.setFriction(0.85).setCollisionGroups(0x0001ffff), ground);
    const focus = wantedFocus ?? city.initialTarget ?? { x: 0, z: 0 };
    const buildings = [
      ...city.buildings,
      ...(city.props ?? []),
      ...(city.bridges ?? []),
      ...(city.infrastructure ?? []),
    ];
    const records = new Map<Building, DormantCluster>();
    const districts = new Map<Building, ReconstructionProgress['districts'][number]>();
    const totals = new Map<string, ReconstructionProgress['districts'][number]>();
    for (const b of buildings) {
      this.buildingIndex.set(b.id, b);
      if (b.parentCell !== undefined) {
        const parent = Math.floor(b.parentCell / 1000000);
        let children = this.parentedByBuilding.get(parent);
        if (!children) this.parentedByBuilding.set(parent, (children = new Set()));
        children.add(b.id);
      }
      this.initialCells += b.cells.length;
      if (staged) {
        let district = city.districts?.find((d) => d.id === b.district);
        if (!district)
          for (const d of city.districts ?? [])
            if (
              !district ||
              Math.hypot(b.x - d.x, b.z - d.z) < Math.hypot(b.x - district.x, b.z - district.z)
            )
              district = d;
        const key = district?.id ?? 'city';
        let row = totals.get(key);
        if (!row) totals.set(key, (row = { id: key, cells: 0, total: 0, phase: -1 }));
        row.total += b.cells.length;
        districts.set(b, row);
        this.reconstruction!.totalCells += b.cells.length;
      }
      if (staged && this.buildingIndex.size % 128 === 0) yield;
    }
    if (staged) {
      this.reconstruction!.districts = [...totals.values()];
      buildings.sort(
        (a, b) => Math.hypot(a.x - focus.x, a.z - focus.z) - Math.hypot(b.x - focus.x, b.z - focus.z),
      );
    }
    for (let phase = staged ? 0 : 3; phase <= 3; phase++) {
      if (staged) {
        this.reconstruction!.phase = phase;
        yield;
      }
      for (const b of buildings) {
        let layout = this.reconstructionLayouts.get(b);
        if (!layout) {
          const parts: Cell[][] = [[], [], [], []];
          for (const cell of b.cells) {
            const stage =
              b.parentCell !== undefined || b.kind === 'tree' || b.kind === 'furniture'
                ? 3
                : b.kind === 'road' || b.kind === 'bridge' || cell.root || cell.y === 0
                  ? 0
                  : cell.material === 'glass' || cell.role === 'glass' || cell.role === 'prop'
                    ? 3
                    : cell.role === 'column' ||
                        cell.role === 'beam' ||
                        cell.role === 'slab' ||
                        cell.role === 'roof'
                      ? 1
                      : 2;
            parts[stage].push(cell);
          }
          layout = { origin: this.origin(b.cells), parts };
          this.reconstructionLayouts.set(b, layout);
        }
        const added = staged ? layout.parts[phase] : b.cells;
        if (!added.length) continue;
        let record = records.get(b);
        let commitStart: number;
        if (record) {
          const pending = new Map(record.cells);
          for (let i = 0; i < added.length; i++) {
            const cell = added[i];
            pending.set(cell.id, { ...cell });
            if (staged && i % 256 === 255) yield;
          }
          const active = this.clusters.get(record.id);
          const prepared = staged
            ? yield* this.prepareRestoredGeometry({ ...record, cells: pending }, !!active)
            : undefined;
          commitStart = performance.now();
          this.dormantNavigation.remove(record.id);
          record.cells = pending;
          for (const cell of added) this.cellOwner.set(cell.id, record.id);
          record.revision = ++this.topologyRevision;
          if (active) {
            active.cells = pending;
            active.revision = record.revision;
            this.rebuild(active, false, prepared);
          } else {
            this.dormantNavigation.add(record, prepared && this.restoredBounds.get(prepared));
            this.drawDormant(record, record.cells.size === b.cells.length, prepared);
          }
        } else {
          const cells = new Map<number, Cell>();
          for (let i = 0; i < added.length; i++) {
            const cell = added[i];
            cells.set(cell.id, { ...cell });
            if (staged && i % 256 === 255) yield;
          }
          const id = this.nextId++;
          const origin = layout.origin;
          const rotation = b.rotation ?? { x: 0, y: 0, z: 0, w: 1 };
          const rotatedOrigin = origin
            .clone()
            .applyQuaternion(new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w));
          record = {
            id,
            revision: ++this.topologyRevision,
            building: b,
            cells,
            anchored: true,
            origin: { ...origin },
            position: {
              x: b.x + rotatedOrigin.x,
              y: (b.baseY ?? GROUND) + rotatedOrigin.y,
              z: b.z + rotatedOrigin.z,
            },
            rotation,
          };
          const nearby =
            Math.hypot(record.position.x - focus.x, record.position.z - focus.z) < radius || city.half < 100;
          const prepared = staged ? yield* this.prepareRestoredGeometry(record, nearby) : undefined;
          commitStart = performance.now();
          records.set(b, record);
          this.dormant.set(id, record);
          this.buildingOwners.set(b.id, new Set([id]));
          this.dormantNavigation.add(record, prepared && this.restoredBounds.get(prepared));
          for (const cell of cells.values()) this.cellOwner.set(cell.id, id);
          if (nearby) {
            const active = this.activate(id, prepared)!;
            record.cells = active.cells;
          } else this.drawDormant(record, record.cells.size === b.cells.length, prepared);
        }
        if (staged) {
          const ms = performance.now() - commitStart;
          if (ms > 8) {
            const jobs = this.reconstruction!.longJobs;
            jobs.push({ phase, building: b.id, kind: b.kind, cells: record.cells.size, ms });
            jobs.sort((a, b) => b.ms - a.ms);
            jobs.length = Math.min(jobs.length, 32);
          }
          this.reconstruction!.cells += added.length;
          const district = districts.get(b)!;
          district.cells += added.length;
          district.phase = phase;
        }
        yield;
      }
    }
    this.world.step(this.events);
    if (staged) {
      this.reconstruction!.phase = 4;
      for (const row of totals.values()) row.phase = 4;
    }
  }
  dispose(keepTemplates = false): void {
    for (const step of this.disposeSteps(keepTemplates)) void step;
  }
  *disposeSteps(keepTemplates = false): Generator<void> {
    if (!keepTemplates) {
      for (const geometry of this.pristineGeometry.values()) geometry.dispose();
      this.pristineGeometry.clear();
      this.resetTemplateBytes = 0;
    }
    this.jobs.clear();
    this.assessments.clear();
    this.cellRegions.clear();
    this.stressQueued = false;
    this.assessmentSliceMs = this.assessmentLatencyMs = 0;
    this.damageInbox.clear();
    this.captureQueue.length = this.captureCells = 0;
    this.pendingActivate.clear();
    this.pendingPhysicsActivate.clear();
    this.collisionPinnedUntil.clear();
    this.dormant.clear();
    this.renderedDormant.clear();
    this.dormantNavigation.clear();
    this.buildingIndex.clear();
    this.parentedByBuilding.clear();
    this.pendingParented.clear();
    this.runtimeStructures.clear();
    this.buildingOwners.clear();
    this.stressed.clear();
    this.pinnedCells.clear();
    for (const c of this.clusters.values()) {
      this.scene.remove(c.mesh);
      c.mesh.geometry.dispose();
      c.geometryCache?.clear();
      this.clusters.delete(c.id);
      yield;
    }
    this.clusters.clear();
    this.dependents.clear();
    this.settlementCursor = undefined;
    this.parkingCursor = undefined;
    yield* this.batches.clearSteps();
    this.dynamicStructuralColliders = 0;
    this.dynamicStructuralCuboids = 0;
    this.colliderOwner.clear();
    this.cellOwner.clear();
    this.events?.free();
    this.world?.free();
  }
  private origin(cells: Iterable<Cell>): THREE.Vector3 {
    const origin = new THREE.Vector3();
    let total = 0;
    for (const cell of cells) {
      const m = cellMass(cell),
        p = physicalCenter(cell);
      origin.addScaledVector(new THREE.Vector3(p.x, p.y, p.z), m);
      total += m;
    }
    return origin.divideScalar(Math.max(0.001, total));
  }
  registerRuntimeStructure(b: Building): Cluster {
    return this.registerRuntimeAssembly(b, [b.cells])[0];
  }
  registerRuntimeAssembly(b: Building, groups: Cell[][]): Cluster[] {
    if (this.runtimeStructures.has(b.id)) throw new Error(`Runtime structure ${b.id} already exists`);
    this.runtimeStructures.add(b.id);
    this.buildingIndex.set(b.id, b);
    this.initialCells += b.cells.length;
    return groups.map((cells) =>
      this.create(
        b,
        cells.map((c) => ({ ...c })),
        false,
      ),
    );
  }
  unregisterRuntimeStructure(id: number): void {
    if (!this.runtimeStructures.delete(id)) return;
    this.structureVersion++;
    this.supportChanged(id);
    const b = this.buildingIndex.get(id);
    for (const owner of [...(this.buildingOwners.get(id) ?? [])]) {
      const c = this.clusters.get(owner);
      if (c) this.remove(c);
      const r = this.dormant.get(owner);
      if (r) {
        const bounds = this.dormantNavigation.bounds(owner);
        if (bounds) this.footGeometry.bounds(bounds.min.x, bounds.min.z, bounds.max.x, bounds.max.z);
        this.reactivateDependents(owner);
        if (r.supportOwner !== undefined) this.dependents.get(r.supportOwner)?.delete(owner);
        for (const cell of r.cells.values()) {
          this.cellOwner.delete(cell.id);
          this.damageInbox.remove(cell.id);
        }
        this.dormant.delete(owner);
        this.dormantNavigation.remove(owner);
        this.batches.remove(owner);
        this.renderedDormant.delete(owner);
      }
    }
    this.buildingOwners.delete(id);
    this.buildingIndex.delete(id);
    if (b) this.initialCells -= b.cells.length;
  }
  create(
    b: Building,
    list: Cell[],
    anchored: boolean,
    source?: Cluster,
    saved?: DormantCluster,
    preparedGeometry?: THREE.BufferGeometry,
  ): Cluster {
    const origin = saved
      ? new THREE.Vector3(saved.origin.x, saved.origin.y, saved.origin.z)
      : this.origin(list);
    const pos = saved
      ? new THREE.Vector3(saved.position.x, saved.position.y, saved.position.z)
      : source
        ? this.toWorld(source, origin)
        : origin.clone().add(new THREE.Vector3(b.x, b.baseY ?? GROUND, b.z));
    const rot = saved?.rotation ?? (source ? source.body.rotation() : { x: 0, y: 0, z: 0, w: 1 });
    const desc =
      anchored || saved?.settled
        ? RAPIER.RigidBodyDesc.fixed()
        : RAPIER.RigidBodyDesc.dynamic()
            .setLinearDamping(0.35)
            .setAngularDamping(0.8)
            .setCcdEnabled(list.length < 40)
            .setCanSleep(true);
    desc.setTranslation(pos.x, pos.y, pos.z).setRotation(rot);
    const body = this.world.createRigidBody(desc);
    const c: Cluster = {
      id: saved?.id ?? this.nextId++,
      revision: saved?.revision ?? ++this.topologyRevision,
      building: b,
      cells: new Map(list.map((v) => [v.id, v])),
      origin,
      body,
      colliders: [],
      colliderKeys: [],
      collisionBoxes: [],
      colliderGroups: anchored || saved?.settled ? 0x0008fff7 : 0x0002ffff,
      mesh: new THREE.Mesh(undefined, this.material),
      anchored,
      previous: pos.clone(),
      previousQ: new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w),
      radius: 1,
      lastImpact: source?.lastImpact ?? -100,
      speedBeforeStep: 0,
      velocityBeforeStep: new THREE.Vector3(),
      angularBeforeStep: new THREE.Vector3(),
      settled: saved?.settled ?? false,
      supportOwner: saved?.supportOwner,
      supportPoint: saved?.supportPoint,
      quietSince: this.time,
      renderSleeping: false,
      detail: true,
    };
    if (source && !anchored) {
      const vel = source.body.velocityAtPoint(pos);
      body.setLinvel(vel, true);
      body.setAngvel(source.body.angvel(), true);
    }
    this.clusters.set(c.id, c);
    let owners = this.buildingOwners.get(b.id);
    if (!owners) this.buildingOwners.set(b.id, (owners = new Set()));
    owners.add(c.id);
    for (const v of list) {
      this.cellOwner.set(v.id, c.id);
      this.damageInbox.remap(v.id, c.id);
    }
    this.rebuild(c, !!saved?.settled, preparedGeometry);
    if (this.life.phase === 'rebuilding') this.scene.add(c.mesh);
    this.syncOne(c, 1);
    if (saved && !saved.anchored && !saved.settled) c.body.sleep();
    return c;
  }
  rebuild(c: Cluster, restoringSettled = false, preparedGeometry?: THREE.BufferGeometry): void {
    this.structureVersion++;
    this.supportChanged(c.building.id);
    this.invalidateFoot(c);
    const phase = this.rebuildProfile;
    let mark = performance.now();
    if (!restoringSettled) {
      this.invalidateParented(c.building.id);
      c.revision = ++this.topologyRevision;
      this.reactivate(c);
      this.reactivateDependents(c.id, c);
    }
    if (c.colliderGroups === 0x0002ffff) {
      this.dynamicStructuralColliders -= c.colliders.length;
      this.dynamicStructuralCuboids -= c.collisionBoxes.length;
    }
    const previous = new Map(c.colliderKeys.map((key, i) => [key, c.colliders[i]]));
    const boxes =
        (preparedGeometry && this.restoredColliders.get(preparedGeometry)) ?? occupiedBoxes(c.cells.values()),
      next: RAPIER.Collider[] = [],
      keys: string[] = [];
    if (preparedGeometry) this.restoredColliders.delete(preparedGeometry);
    const groups = c.anchored || c.settled ? 0x0008fff7 : 0x0002ffff;
    let retained = 0,
      created = 0;
    for (const group of occupiedColliderGroups(boxes)) {
      const key = group.key;
      keys.push(key);
      const existing = previous.get(key);
      if (existing) {
        // Preserve exact unchanged occupied cuboids and their warm contact
        // manifolds. Only removed support wakes its touching dynamic bodies.
        if (c.colliderGroups !== groups) existing.setCollisionGroups(groups);
        next.push(existing);
        previous.delete(key);
        retained++;
        continue;
      }
      const desc = occupiedColliderDesc(group, c.origin)
        .setFriction(0.82)
        .setCollisionGroups(groups)
        .setRestitution(0.025)
        .setDensity(0)
        .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS)
        .setContactForceEventThreshold(900);
      const shape = this.world.createCollider(desc, c.body);
      next.push(shape);
      this.colliderOwner.set(shape.handle, c.id);
      created++;
    }
    for (const shape of previous.values()) {
      this.colliderOwner.delete(shape.handle);
      this.world.removeCollider(shape, true);
    }
    c.colliders = next;
    c.colliderKeys = keys;
    c.colliderGroups = groups;
    c.collisionBoxes = boxes;
    phase.retained = Math.max(phase.retained, retained);
    phase.created = Math.max(phase.created, created);
    phase.removed = Math.max(phase.removed, previous.size);
    if (!c.anchored && !c.settled) {
      this.dynamicStructuralColliders += c.colliders.length;
      this.dynamicStructuralCuboids += c.collisionBoxes.length;
    }
    phase.colliders = Math.max(phase.colliders, performance.now() - mark);
    mark = performance.now();
    c.mesh.geometry.dispose();
    let sections: Map<string, THREE.BufferGeometry> | undefined;
    const visualCells = this.visibleCells(c);
    if (preparedGeometry) {
      c.geometryCache?.clear();
      c.geometryCache = undefined;
      c.mesh.geometry = preparedGeometry;
    } else if (c.anchored && visualCells.size > 256 && this.life.phase === 'playing' && !this.glassBand(c)) {
      c.geometryCache ??= new BuildingGeometryCache();
      const cutaway =
        this.cutaways.has(c.building.id) ||
        (c.building.parentCell !== undefined &&
          this.cutaways.has(Math.floor(c.building.parentCell / 1000000)));
      const result = c.geometryCache.rebuild(c.building, visualCells, c.origin, c.detail, cutaway);
      c.mesh.geometry = result.geometry;
      sections = result.parts;
    } else {
      c.geometryCache?.clear();
      c.geometryCache = undefined;
      c.mesh.geometry = buildingGeometry(
        c.building,
        visualCells,
        c.origin,
        c.detail,
        undefined,
        this.glassBand(c),
      );
    }
    phase.geometry = Math.max(phase.geometry, performance.now() - mark);
    mark = performance.now();
    phase.cells = Math.max(phase.cells, c.cells.size);
    c.mesh.castShadow = true;
    c.mesh.receiveShadow = true;
    let bounds = this.restoredBounds.get(c.mesh.geometry);
    if (!bounds) {
      const work = occupiedBoundsSteps(c.cells.values(), c.origin);
      let result = work.next();
      while (!result.done) result = work.next();
      bounds = result.value;
    }
    this.restoredBounds.delete(c.mesh.geometry);
    c.occupiedBounds = bounds;
    const sphere = bounds.getBoundingSphere(new THREE.Sphere());
    // Body origin stays fixed when a surviving cluster loses an asymmetric part.
    // A sphere measured around the geometry centre is not a bound around that origin.
    c.radius = sphere ? sphere.radius + sphere.center.length() : 1;
    this.invalidateFoot(c);
    const properties = this.restoredMass.get(c.mesh.geometry) ?? aggregateMass(c.cells.values(), c.origin);
    this.restoredMass.delete(c.mesh.geometry);
    const localCom = c.origin.clone().add(properties.center),
      newWorldCom = this.toWorld(c, localCom);
    const velocity = c.body.velocityAtPoint(newWorldCom);
    c.body.setAdditionalMassProperties(
      properties.mass,
      properties.center,
      properties.inertia,
      properties.frame,
      true,
    );
    c.body.recomputeMassPropertiesFromColliders();
    if (!c.anchored) c.body.setLinvel(velocity, true);
    // This rebuild changes shapes/mass, not body position. New colliders inherit
    // their body's current transform; World.step synchronizes moved bodies.
    phase.mass = Math.max(phase.mass, performance.now() - mark);
    mark = performance.now();
    this.syncOne(c, 1);
    c.mesh.updateMatrix();
    if (sections) this.batches.setSections(c.id, sections, c.mesh.matrix);
    else
      this.batches.set(
        c.id,
        c.mesh.geometry,
        c.mesh.matrix,
        c.anchored || c.settled,
        this.restoredUploads.get(c.mesh.geometry),
      );
    this.restoredUploads.delete(c.mesh.geometry);
    phase.batch = Math.max(phase.batch, performance.now() - mark);
    c.mesh.visible = this.life.phase !== 'playing';
  }
  remove(c: Cluster, parking = false): void {
    this.structureVersion++;
    this.supportChanged(c.building.id);
    this.invalidateFoot(c);
    c.geometryCache?.clear();
    if (!parking) {
      this.invalidateParented(c.building.id);
      this.reactivateDependents(c.id);
      if (c.supportOwner !== undefined) this.dependents.get(c.supportOwner)?.delete(c.id);
      this.batches.remove(c.id);
    }
    if (!c.anchored && !c.settled) {
      this.dynamicStructuralColliders -= c.colliders.length;
      this.dynamicStructuralCuboids -= c.collisionBoxes.length;
    }
    this.clusters.delete(c.id);
    if (!parking) this.buildingOwners.get(c.building.id)?.delete(c.id);
    this.collisionPinnedUntil.delete(c.id);
    this.stressed.delete(c.id);
    this.assessments.delete(c.id);
    if (!parking)
      for (const cell of c.cells.values())
        if (this.cellOwner.get(cell.id) === c.id) {
          this.cellOwner.delete(cell.id);
          this.damageInbox.remove(cell.id);
        }
    for (const col of c.colliders) this.colliderOwner.delete(col.handle);
    this.scene.remove(c.mesh);
    c.mesh.geometry.dispose();
    this.world.removeRigidBody(c.body);
  }
  private exteriorMasks = new WeakMap<Building, Set<number>>();
  private visibleCells(c: Pick<Cluster, 'anchored' | 'building' | 'cells'>): ReadonlyMap<number, Cell> {
    const b = c.building;
    if (
      this.city.streetLife &&
      b.exterior &&
      c.anchored &&
      c.cells.size === b.cells.length &&
      !this.cutaways.has(b.id) &&
      ![...c.cells.values()].some((cell) => cell.fracture)
    ) {
      let mask = this.exteriorMasks.get(b);
      if (!mask) {
        mask = exteriorCellMask(b);
        this.exteriorMasks.set(b, mask);
      }
      // Floors/supports remain authoritative and damageable. Only a complete
      // opaque shell omits its hidden internal geometry. A hole or detached
      // cluster immediately renders the original newly exposed structure.
      return new Map([...c.cells].filter(([id]) => mask!.has(id)));
    }
    if (!c.anchored || this.life.phase === 'rebuilding') return c.cells;
    const plane = this.cutaways.get(c.building.id);
    if (plane !== undefined)
      return new Map(
        [...c.cells].filter(([, cell]) => cell.y <= plane && !(cell.z === 0 && cell.y >= plane - 1)),
      );
    if (c.building.parentCell !== undefined) {
      const parent = Math.floor(c.building.parentCell / 1000000),
        height = this.cutaways.get(parent),
        b = this.buildingIndex.get(parent);
      if (height !== undefined && b && (c.building.baseY ?? 0) > (b.baseY ?? GROUND) + (height + 1) * CELL)
        return new Map();
      if (height !== undefined && b && c.building.furniture === 'partition') {
        const top = ((b.baseY ?? GROUND) + (height + 1) * CELL - (c.building.baseY ?? 0)) / CELL;
        return new Map(
          [...c.cells].map(([id, cell]) => [
            id,
            {
              ...cell,
              parts: cell.parts
                ?.map((p) => ({ ...p, h: Math.min(p.h, Math.max(0, top - cell.y - p.y)) }))
                .filter((p) => p.h > 0),
            },
          ]),
        );
      }
    }
    return c.cells;
  }
  private glassBand(c: Pick<Cluster, 'anchored' | 'building'>): { low: number; high: number } | undefined {
    return c.anchored && this.life.phase === 'playing' && this.glazed?.buildingId === c.building.id
      ? this.glazed
      : undefined;
  }
  buildingViewOpened(id: number): boolean {
    return this.life.phase !== 'playing' || this.cutaways.has(id) || this.glazed?.buildingId === id;
  }
  setCutaway(buildingId?: number, floor = 0, throughGlass = false): void {
    const affected = new Set(this.cutaways.keys());
    if (this.glazed) affected.add(this.glazed.buildingId);
    if (buildingId !== undefined) affected.add(buildingId);
    this.cutaways.clear();
    this.glazed = undefined;
    const spec = this.city.interiors?.find((r) => r.buildingId === buildingId);
    if (spec && throughGlass)
      this.glazed = {
        buildingId: spec.buildingId,
        low: floor * spec.floorHeight + 1,
        high: (floor + 1) * spec.floorHeight - 1,
      };
    else if (spec) this.cutaways.set(spec.buildingId, floor * spec.floorHeight + 1);
    else if (this.city.facilities?.some((f) => f.buildingId === buildingId))
      this.cutaways.set(buildingId!, 0);
    if (buildingId !== undefined) this.activateBuilding(buildingId);
    const matches = (b: Building) =>
      affected.has(b.id) || (b.parentCell !== undefined && affected.has(Math.floor(b.parentCell / 1e6)));
    for (const c of this.clusters.values())
      if (c.anchored && matches(c.building)) {
        c.mesh.geometry.dispose();
        c.geometryCache?.clear();
        c.geometryCache = undefined;
        c.mesh.geometry = buildingGeometry(
          c.building,
          this.visibleCells(c),
          c.origin,
          c.detail,
          undefined,
          this.glassBand(c),
        );
        this.batches.set(c.id, c.mesh.geometry, c.mesh.matrix, true);
      }
    for (const r of this.dormant.values()) if (matches(r.building)) this.drawDormant(r);
  }
  toWorld(c: Cluster, local: Vec): THREE.Vector3 {
    const r = c.body.rotation(),
      p = c.body.translation();
    return new THREE.Vector3(local.x, local.y, local.z)
      .sub(c.origin)
      .applyQuaternion(new THREE.Quaternion(r.x, r.y, r.z, r.w))
      .add(new THREE.Vector3(p.x, p.y, p.z));
  }
  toLocal(c: Cluster, world: Vec): THREE.Vector3 {
    const r = c.body.rotation(),
      p = c.body.translation();
    return new THREE.Vector3(world.x - p.x, world.y - p.y, world.z - p.z)
      .applyQuaternion(new THREE.Quaternion(r.x, r.y, r.z, r.w).invert())
      .add(c.origin);
  }
  hit(c: Cluster, point: Vec, radius: number, energy: number, direction?: Vec): number {
    return this.applyHits(c, [{ point, radius, energy, direction }]);
  }
  private scheduleHit(
    c: Cluster,
    point: Vec,
    radius: number,
    energy: number,
    direction?: Vec,
    impulseScale = 1,
  ): void {
    const local = this.toLocal(c, point);
    this.captureDamage(c.id, this.cellRegions.query(c.cells, local, radius), local, {
      point: { ...point },
      radius,
      energy,
      direction,
      impulseScale,
    });
  }
  private captureDamage(owner: number, cells: Iterable<Cell>, local: Vec, request: DamageRequest): void {
    const { radius, energy } = request;
    for (const cell of cells) {
      const currentOwner = this.cellOwner.get(cell.id);
      if (currentOwner === undefined) continue;
      const distance = cellDistance(cell, local);
      if (distance <= radius)
        this.damageInbox.add(
          cell.id,
          currentOwner ?? owner,
          energy * (request.multiplicity ?? 1) * (0.3 + 0.7 * (1 - distance / Math.max(0.01, radius))),
          request,
        );
    }
  }
  /** Capture a wave's cells now, including dormant transforms, without eager activation. */
  damageBuilding(id: number, point: Vec, radius: number, energy: number): void {
    for (const owner of this.buildingOwners.get(id) ?? []) {
      const c = this.clusters.get(owner);
      if (c) {
        this.scheduleHit(c, point, radius, energy);
        continue;
      }
      const r = this.dormant.get(owner);
      if (r) {
        const q = r.rotation;
        const local = new THREE.Vector3(
          point.x - r.position.x,
          point.y - r.position.y,
          point.z - r.position.z,
        )
          .applyQuaternion(new THREE.Quaternion(q.x, q.y, q.z, q.w).invert())
          .add(new THREE.Vector3(r.origin.x, r.origin.y, r.origin.z));
        this.captureDamage(r.id, this.cellRegions.query(r.cells, local, radius), local, {
          point: { ...point },
          radius,
          energy,
        });
      }
    }
  }
  ground(
    point: Vec,
    up = 0.28,
    down = 0.45,
    exclude?: RAPIER.RigidBody,
  ): { y: number; owner: number; normal: Vec } | undefined {
    const ray = new RAPIER.Ray({ x: point.x, y: point.y + up, z: point.z }, { x: 0, y: -1, z: 0 });
    let hit: ReturnType<RAPIER.World['castRayAndGetNormal']> = null;
    let sides: Set<number> | undefined;
    // A sloped strip's exposed end face can be the nearest ray hit while a
    // load-bearing neighbour lies immediately beneath. Do not turn that side
    // contact into a false floor loss; still require a real upward surface.
    for (let probe = 0; probe < 4; probe++) {
      hit = this.world.castRayAndGetNormal(
        ray,
        up + down,
        false,
        RAPIER.QueryFilterFlags.EXCLUDE_KINEMATIC,
        undefined,
        undefined,
        exclude,
        sides ? (c) => !sides!.has(c.handle) : undefined,
      );
      if (!hit || hit.normal.y > 0.65) break;
      (sides ??= new Set()).add(hit.collider.handle);
    }
    let result =
      hit && hit.normal.y > 0.65
        ? {
            y: point.y + up - hit.timeOfImpact,
            owner: this.colliderOwner.get(hit.collider.handle) ?? -1,
            normal: hit.normal,
          }
        : undefined;
    const dormant = this.dormantNavigation.ground(point, up, down);
    if (dormant && (!result || dormant.y > result.y)) result = dormant;
    return result;
  }
  private flatSupport(point: Vec): FlatSupport | undefined {
    const ground = this.ground(point, 0.35, 0.48);
    if (!ground || Math.abs(ground.y - point.y) >= 0.00005) return;
    if (ground.owner === -1) {
      const patch = this.city.terrain
        ? flatTerrainPatch(this.city.terrain, point.x, point.z)
        : {
            minX: -this.city.half - 8,
            maxX: this.city.half + 8,
            minZ: -this.city.half - 8,
            maxZ: this.city.half + 8,
            y: GROUND,
          };
      if (patch && Math.abs(patch.y - ground.y) < 0.00005) return { ...patch, owner: -1 };
    }
    const c = this.clusters.get(ground.owner);
    if (!c) return this.dormantNavigation.flatSupport(ground.owner, point, ground.y);
    if (!c.anchored && !c.settled) return;
    const q = c.body.rotation(),
      sin = 2 * q.y * q.w,
      cos = 1 - 2 * q.y * q.y;
    if (
      Math.abs(q.x) > 1e-8 ||
      Math.abs(q.z) > 1e-8 ||
      (Math.abs(sin) > 1e-7 && Math.abs(Math.abs(sin) - 1) > 1e-7)
    )
      return;
    const p = c.body.translation(),
      dx = point.x - p.x,
      dz = point.z - p.z,
      x = cos * dx - sin * dz + c.origin.x,
      z = sin * dx + cos * dz + c.origin.z;
    for (const b of c.collisionBoxes) {
      if (
        Math.abs((b.y + b.h) * CELL - c.origin.y + p.y - ground.y) > 0.00005 ||
        x < b.x * CELL ||
        x > (b.x + b.w) * CELL ||
        z < b.z * CELL ||
        z > (b.z + b.d) * CELL
      )
        continue;
      const bx = (b.x + b.w / 2) * CELL - c.origin.x,
        bz = (b.z + b.d / 2) * CELL - c.origin.z,
        cx = p.x + cos * bx + sin * bz,
        cz = p.z - sin * bx + cos * bz,
        hx = ((Math.abs(cos) * b.w + Math.abs(sin) * b.d) * CELL) / 2,
        hz = ((Math.abs(sin) * b.w + Math.abs(cos) * b.d) * CELL) / 2;
      return { owner: c.id, y: ground.y, minX: cx - hx, maxX: cx + hx, minZ: cz - hz, maxZ: cz + hz };
    }
  }
  /** A cached resting actor is valid only while the same support stays fixed. */
  staticSupportVersion(owner: number): number | undefined {
    if (owner === -1) return 0;
    const c = this.clusters.get(owner) ?? this.dormant.get(owner);
    return c && (c.anchored || c.settled) ? c.revision : undefined;
  }
  sweepFoot(point: Vec, move: Vec, radius = 0.24, cache?: FootCache, stepHeight = 0.35): number {
    if (stepHeight > 0.35) cache = undefined;
    if (this.footClearance.obstructed(point, move, radius, cache)) return 0;
    const shape = new RAPIER.Capsule(0.58, radius),
      rotation = { x: 0, y: 0, z: 0, w: 1 };
    const cast = (p: Vec, m: Vec, fixed = false) => {
      const a = this.world.castShape(
          p,
          rotation,
          m,
          shape,
          0.015,
          1,
          false,
          fixed ? RAPIER.QueryFilterFlags.ONLY_FIXED : undefined,
        ),
        b = this.dormantNavigation.sweepCapsule(p, m, radius);
      return a && a.time_of_impact < (b?.time ?? 1) ? { time: a.time_of_impact, normal: a.normal1 } : b;
    };
    const start = { x: point.x, y: point.y + 0.86, z: point.z },
      hit = cast(start, move);
    if (!hit) return 1;
    const ceiling = cast(start, { x: 0, y: stepHeight, z: 0 }),
      forward = cast({ ...start, y: start.y + stepHeight }, { ...move, y: 0 });
    const support = this.ground(
      { x: point.x + move.x, y: point.y, z: point.z + move.z },
      stepHeight,
      stepHeight + 0.1,
    );
    if ((!ceiling || ceiling.normal.y > -0.5) && !forward && support) return 1;
    if (cache && hit.time < 0.015) {
      const solid = cast(start, move, true);
      if (solid && solid.time < 0.015) {
        const fixedCeiling = cast(start, { x: 0, y: 0.35, z: 0 }, true),
          fixedForward = cast({ ...start, y: start.y + 0.35 }, { ...move, y: 0 }, true);
        if ((fixedCeiling && fixedCeiling.normal.y <= -0.5) || fixedForward)
          this.footClearance.rememberObstruction(point, move, radius, cache);
      }
    }
    return Math.max(0, hit.time - 0.005);
  }
  /** Reserved furniture traversal uses the actual turning/lying body envelope,
   * rather than an upright walker extending through the ceiling above a bed. */
  interactionClear(
    from: Vec,
    to: Vec,
    fromYaw: number,
    toYaw: number,
    fromTilt: number,
    toTilt: number,
    scale: number,
    excludeBuilding?: number,
  ): boolean {
    const start = new THREE.Vector3(from.x, from.y, from.z),
      end = new THREE.Vector3(to.x, to.y, to.z),
      a = new THREE.Quaternion()
        .setFromAxisAngle(new THREE.Vector3(0, 1, 0), fromYaw)
        .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), fromTilt)),
      b = new THREE.Quaternion()
        .setFromAxisAngle(new THREE.Vector3(0, 1, 0), toYaw)
        .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), toTilt)),
      count = Math.max(1, Math.ceil(start.distanceTo(end) / 0.12 + a.angleTo(b) / 0.12)),
      shape = new RAPIER.Capsule(0.51 * scale, 0.23 * scale);
    for (let i = 1; i <= count; i++) {
      const t = i / count,
        q = a.clone().slerp(b, t),
        p = start
          .clone()
          .lerp(end, t)
          .add(new THREE.Vector3(0, 0.76 * scale, 0).applyQuaternion(q));
      if (
        this.world.intersectionWithShape(
          p,
          q,
          shape,
          undefined,
          undefined,
          undefined,
          undefined,
          (collider) =>
            excludeBuilding === undefined ||
            this.clusters.get(this.colliderOwner.get(collider.handle) ?? -1)?.building.id !== excludeBuilding,
        ) ||
        this.dormantNavigation.intersectsShape(p, shape, q, 0.75 * scale, excludeBuilding)
      )
        return false;
    }
    return true;
  }
  private sections(groups: Cell[][], small = false): Cell[][] {
    const room = Math.max(12, PRESETS[this.preset].bodies - this.dynamicCount);
    const total = groups.reduce((n, group) => n + group.length, 0);
    const target = Math.max(
      (small ? WORKLOAD.fragmentCells : WORKLOAD.collapseCells) * this.fragmentScale,
      Math.ceil(total / room),
    );
    // Local coherent masonry sections retain their cells and real collision.
    // Do not repartition a pressured solver back into dozens of 16-cell bits.
    // Disconnected support islands remain separate rigid bodies.
    return groups.flatMap((group) => partition(group, Math.round(target)));
  }
  private applyHits(c: Cluster, hits: DamageRequest[]): number {
    if (!this.clusters.has(c.id) || this.life.phase !== 'playing') return 0;
    this.reactivate(c);
    const start = performance.now(),
      removed: Cell[] = [];
    let energy = 0,
      impulseScale = 0;
    for (const h of hits) {
      if (h.damageByCell) {
        for (const [id, amount] of h.damageByCell) {
          const cell = c.cells.get(id);
          if (!cell) continue;
          this.beforeCellChange(cell);
          cell.hp -= amount;
          if (cell.hp <= 0) {
            c.cells.delete(id);
            removed.push(cell);
          }
        }
      } else {
        const result = damage(
          c.cells,
          this.toLocal(c, h.point),
          h.radius,
          h.energy,
          this.cellObservers.size ? this.beforeCellChange : undefined,
        );
        removed.push(...result.removed);
      }
      energy = Math.max(energy, h.energy);
      impulseScale = Math.max(impulseScale, h.impulseScale ?? 1);
    }
    this.damagedCells += removed.length;
    const last = hits[hits.length - 1];
    const retained: Cell[] = [];
    for (const cell of removed) {
      this.cellOwner.delete(cell.id);
      const pulverize =
        energy > 100 ||
        (cell.fracture ?? 0) >= 2 ||
        cell.material === 'glass' ||
        (cell.material === 'foliage' ? cell.id % 3 === 0 : (cell.fracture ?? 0) > 0 && cell.id % 6 === 0);
      if (pulverize) this.removed++;
      else {
        cell.fracture = (cell.fracture ?? 0) + 1;
        cell.hp = MATERIALS[cell.material].strength * 0.66;
        refineFragment(cell, c.building);
        retained.push(cell);
      }
    }
    if (removed.length) {
      this.onParticles(last.point, removed[0].color, Math.min(24, removed.length), Math.min(8, energy));
      this.onChanged(c.building.id);
    }
    // Convert impact material before discarding its former owner: every retained
    // cell belongs to exactly one physical cluster, with no overlapping copy.
    for (const list of this.sections(components(new Map(retained.map((v) => [v.id, v]))), true)) {
      if (!list.length) continue;
      // Tiny blasted chips are visual dust, not a new collision island each.
      // Large slabs and masonry stay persistent, targetable and re-fracturable.
      if (list.length <= (IS_LITE ? 12 : 4)) {
        this.removed += list.length;
        continue;
      }
      const fragment = this.create(c.building, list, false, c);
      this.impulse(fragment, last.point, last.direction, Math.min(5, energy * 0.28) * impulseScale);
    }
    if (!c.cells.size) this.remove(c);
    else if (c.anchored) {
      this.detachUnsupported(c, last.point, last.direction, energy * impulseScale, removed.length > 0);
      if (this.clusters.has(c.id)) this.requestAssessment(c);
    } else {
      const groups = components(c.cells);
      if (groups.length > 1) {
        for (const group of this.sections(groups)) {
          const child = this.create(c.building, group, false, c);
          this.impulse(child, last.point, last.direction, Math.min(2, energy * 0.1) * impulseScale);
        }
        this.remove(c);
      } else {
        if (removed.length) this.rebuild(c);
        this.impulse(c, last.point, last.direction, Math.min(4, energy * 0.25) * impulseScale);
      }
    }
    this.structuralMs += performance.now() - start;
    return removed.length;
  }
  private detachUnsupported(c: Cluster, point: Vec, direction?: Vec, energy = 0, dirty = true): void {
    if (!this.clusters.has(c.id)) return;
    const loose = components(c.cells).filter((group) => !group.some(isRoot));
    if (loose.length) {
      this.onDisturbance({ point, radius: Math.max(8, c.radius), strength: 1, kind: 'collapse' });
      for (const list of this.sections(loose)) {
        for (const cell of list) {
          this.beforeCellChange(cell);
          c.cells.delete(cell.id);
        }
        const child = this.create(c.building, list, false, c);
        if (energy > 0) this.impulse(child, point, direction, Math.min(2, energy * 0.12));
      }
    }
    if (c.cells.size) {
      if (dirty || loose.length) this.rebuild(c);
    } else this.remove(c);
  }
  assess(c: Cluster): StructuralAssessment {
    const start = performance.now();
    const result = evaluateStructure(c.cells);
    this.rebuildProfile.assessment = Math.max(this.rebuildProfile.assessment, performance.now() - start);
    c.stress = result;
    if (result.overloads.length) this.stressed.add(c.id);
    else this.stressed.delete(c.id);
    return result;
  }
  /** Small members remain immediate. A large tower never runs its whole graph
   * in one heat callback or adds duplicate graph jobs to the general queue. */
  requestAssessment(c: Cluster): void {
    if (!c.anchored || !this.clusters.has(c.id)) return;
    if (c.cells.size < 2048) {
      this.assess(c);
      return;
    }
    const pending = this.assessments.get(c.id);
    if (pending?.revision === c.revision) {
      pending.repeat = true;
      return;
    }
    this.stressed.delete(c.id);
    c.stress = undefined;
    this.assessments.set(c.id, {
      revision: c.revision,
      work: evaluateStructureSteps(c.cells, { topologyVersion: c.revision }),
      started: performance.now(),
      repeat: false,
    });
  }
  private processAssessments(deadline: number): void {
    while (this.assessments.size && performance.now() < deadline) {
      const [id, task] = this.assessments.entries().next().value!;
      this.assessments.delete(id);
      const c = this.clusters.get(id);
      if (!c?.anchored) continue;
      if (c.revision !== task.revision) {
        this.requestAssessment(c);
        continue;
      }
      const start = performance.now(),
        result = task.work.next();
      this.assessmentSliceMs = Math.max(this.assessmentSliceMs, performance.now() - start);
      if (!result.done) this.assessments.set(id, task);
      else {
        this.assessmentLatencyMs = Math.max(this.assessmentLatencyMs, performance.now() - task.started);
        c.stress = result.value;
        if (c.stress.overloads.length) this.stressed.add(id);
        else this.stressed.delete(id);
        // A new heat request during the calculation gets another current pass.
        // Preserve this valid result for progressive failure in the meantime.
        if (task.repeat)
          this.assessments.set(id, {
            revision: c.revision,
            work: evaluateStructureSteps(c.cells, { topologyVersion: c.revision }),
            started: performance.now(),
            repeat: false,
          });
      }
    }
  }
  private progressFailure(): void {
    // Bounded progressive processing, only members whose last evaluation is
    // overloaded. Compression/bending failures continue after a blast finishes.
    for (const id of [...this.stressed].slice(0, 3)) {
      const c = this.clusters.get(id);
      if (!c || !c.anchored) {
        this.stressed.delete(id);
        continue;
      }
      const stress = c.stress;
      if (!stress) {
        this.requestAssessment(c);
        continue;
      }
      const broken: Cell[] = [];
      // Break a coherent group of proven overloaded members in one topology
      // transaction instead of rebuilding the entire tower for eight cells.
      for (const o of stress.overloads.slice(0, IS_LITE ? 96 : 64)) {
        const cell = c.cells.get(o.id);
        if (!cell) continue;
        this.beforeCellChange(cell);
        cell.hp -= Math.min(MATERIALS[cell.material].strength * 0.48, (o.ratio - 1) * 0.15);
        if (cell.hp <= 0) {
          broken.push(cell);
        }
      }
      if (broken.length) {
        this.structuralFailures += broken.length;
        // A beam/panel or multipart cell need not occupy its grid centre (or
        // even its centre of mass). Fail the proven overloaded ID exactly once.
        // Gravity drives this collapse; there is no new explosion impulse.
        this.applyHits(c, [
          {
            point: this.toWorld(c, physicalCenter(broken[0])),
            radius: 0.08,
            energy: 0,
            impulseScale: 0,
            damageByCell: new Map(broken.map((cell) => [cell.id, 0])),
          },
        ]);
      } else this.requestAssessment(c);
      this.stressed.delete(id);
      if (this.clusters.has(id) && c.stress?.overloads.length) this.stressed.add(id);
    }
  }
  impulse(c: Cluster, point: Vec, direction?: Vec, speed = 1): void {
    if (c.anchored || speed <= 0) return;
    this.reactivate(c);
    const p = c.body.translation();
    const d = direction
      ? new THREE.Vector3(direction.x, direction.y, direction.z)
      : new THREE.Vector3(p.x - point.x, 0.6, p.z - point.z).normalize();
    d.multiplyScalar(c.body.mass() * speed);
    c.body.applyImpulseAtPoint(d, point, true);
    const v = c.body.linvel(),
      m = Math.hypot(v.x, v.y, v.z);
    if (m > 18) c.body.setLinvel({ x: (v.x / m) * 18, y: (v.y / m) * 18, z: (v.z / m) * 18 }, true);
    const a = c.body.angvel(),
      am = Math.hypot(a.x, a.y, a.z);
    if (am > 3) c.body.setAngvel({ x: (a.x / am) * 3, y: (a.y / am) * 3, z: (a.z / am) * 3 }, true);
  }
  blast(
    point: Vec,
    radius: number,
    energy: number,
    queued = true,
    source: BlastEvent['source'] = 'c4',
    heat = 0.65,
    multiplicity = 1,
  ): void {
    this.onDisturbance({ point, radius: radius + 12, strength: energy / 5, kind: 'explosion' });
    this.emitBlast(point, radius, energy, source, heat, undefined, multiplicity);
    const request: DamageRequest = { point: { ...point }, radius, energy, multiplicity };
    for (const r of [...this.dormant.values()])
      if (
        Math.hypot(r.position.x - point.x, r.position.z - point.z) <
        radius + Math.hypot(r.building.w, r.building.d) * CELL
      )
        if (!queued) this.activate(r.id);
        else {
          const q = r.rotation;
          const local = new THREE.Vector3(
            point.x - r.position.x,
            point.y - r.position.y,
            point.z - r.position.z,
          )
            .applyQuaternion(new THREE.Quaternion(q.x, q.y, q.z, q.w).invert())
            .add(new THREE.Vector3(r.origin.x, r.origin.y, r.origin.z));
          this.enqueueCapture(r.id, r.cells, local, request);
        }
    for (const c of [...this.clusters.values()]) {
      const p = c.body.translation();
      if (Math.hypot(p.x - point.x, p.y - point.y, p.z - point.z) > radius + c.radius) continue;
      if (queued) this.enqueueCapture(c.id, c.cells, this.toLocal(c, point), request);
      else this.hit(c, point, radius, energy);
    }
  }
  emitBlast(
    point: Vec,
    radius: number,
    energy: number,
    source: BlastEvent['source'],
    heat: number,
    id = ++this.eventId,
    multiplicity = 1,
  ): number {
    this.onBlast({
      id,
      epoch: this.life.epoch,
      point: { ...point },
      radius,
      energy,
      heat,
      source,
      multiplicity,
    });
    return id;
  }
  private enqueueCapture(owner: number, cells: Map<number, Cell>, local: Vec, request: DamageRequest): void {
    const candidates = [...this.cellRegions.query(cells, local, request.radius)];
    if (!candidates.length) return;
    this.captureQueue.push({
      epoch: this.life.epoch,
      owner,
      cells: candidates,
      local,
      request,
      cursor: 0,
      queuedAt: performance.now(),
    });
    this.captureCells += candidates.length;
  }
  get captureJobs(): number {
    return this.captureQueue.length;
  }
  get captureAgeMs(): number {
    return this.captureQueue.length ? performance.now() - this.captureQueue[0].queuedAt : 0;
  }
  clearCaptures(): void {
    this.captureQueue.length = this.captureCells = 0;
  }
  activate(id: number, preparedGeometry?: THREE.BufferGeometry): Cluster | undefined {
    const existing = this.clusters.get(id);
    if (existing) return existing;
    const r = this.dormant.get(id);
    if (!r) return;
    this.dormant.delete(id);
    this.renderedDormant.delete(id);
    this.dormantNavigation.remove(id);
    this.streamedIn++;
    return this.create(r.building, [...r.cells.values()], r.anchored, undefined, r, preparedGeometry);
  }
  private restoredUploads = new WeakMap<THREE.BufferGeometry, StaticUpload>();
  private restoredBounds = new WeakMap<THREE.BufferGeometry, THREE.Box3>();
  private restoredMass = new WeakMap<THREE.BufferGeometry, ReturnType<typeof aggregateMass>>();
  private restoredColliders = new WeakMap<THREE.BufferGeometry, Box[]>();
  private *prepareRestoredGeometry(
    r: DormantCluster,
    detail: boolean,
  ): Generator<void, THREE.BufferGeometry> {
    const preparing = (operation: string) => {
      if (this.reconstruction) this.reconstruction.preparing = { building: r.building.id, operation };
    };
    preparing('visible geometry');
    const cached =
      !detail && r.cells.size === r.building.cells.length && !this.cutaways.size && !this.glassBand(r)
        ? this.pristineGeometry.get(r.building)
        : undefined;
    const geometry =
      cached ??
      (yield* buildingGeometrySteps(
        r.building,
        this.visibleCells(r),
        r.origin,
        detail,
        undefined,
        this.glassBand(r),
      ));
    if (geometry.getAttribute('position').count > 16384) {
      preparing('static vertex transforms');
      yield;
      const matrix = new THREE.Matrix4().compose(
        new THREE.Vector3(r.position.x, r.position.y, r.position.z),
        new THREE.Quaternion(r.rotation.x, r.rotation.y, r.rotation.z, r.rotation.w),
        new THREE.Vector3(1, 1, 1),
      );
      this.restoredUploads.set(geometry, yield* this.batches.prepareFixed(geometry, matrix));
    }
    if (r.cells.size > 4096) {
      preparing('occupied bounds');
      yield;
      this.restoredBounds.set(geometry, yield* occupiedBoundsSteps(r.cells.values(), r.origin, 2048));
      if (detail) {
        preparing('mass and inertia');
        yield;
        this.restoredMass.set(geometry, yield* aggregateMassSteps(r.cells.values(), r.origin));
        preparing('occupied colliders');
        yield;
        this.restoredColliders.set(geometry, yield* occupiedBoxesSteps(r.cells.values()));
      }
    }
    preparing('publish cells and collision');
    yield;
    return geometry;
  }
  private drawDormant(r: DormantCluster, authored = false, preparedGeometry?: THREE.BufferGeometry): void {
    // These CPU-only templates are bounded to the authored city. Damaged,
    // streamed, cutaway and runtime geometry never enters the reset cache.
    const cacheable =
      authored &&
      !this.cutaways.has(r.building.id) &&
      (r.building.parentCell === undefined || !this.cutaways.has(Math.floor(r.building.parentCell / 1e6))) &&
      !this.glassBand(r);
    const geometry =
      preparedGeometry ??
      (cacheable ? this.pristineGeometry.get(r.building) : undefined) ??
      buildingGeometry(r.building, this.visibleCells(r), r.origin, false, undefined, this.glassBand(r));
    const matrix = new THREE.Matrix4().compose(
      new THREE.Vector3(r.position.x, r.position.y, r.position.z),
      new THREE.Quaternion(r.rotation.x, r.rotation.y, r.rotation.z, r.rotation.w),
      new THREE.Vector3(1, 1, 1),
    );
    this.batches.set(r.id, geometry, matrix, true, this.restoredUploads.get(geometry));
    this.restoredUploads.delete(geometry);
    this.restoredBounds.delete(geometry);
    if (cacheable) {
      if (!this.pristineGeometry.has(r.building))
        this.resetTemplateBytes += Object.values(geometry.attributes).reduce(
          (n, a) => n + a.array.byteLength,
          0,
        );
      this.pristineGeometry.set(r.building, geometry);
    } else geometry.dispose();
    this.renderedDormant.add(r.id);
  }
  activateBuilding(id: number): Cluster[] {
    for (const r of [...this.dormant.values()]) if (r.building.id === id) this.activate(r.id);
    return [...this.clusters.values()].filter((c) => c.building.id === id);
  }
  buildingCells(id: number): Cell[] {
    const result: Cell[] = [];
    for (const owner of this.buildingOwners.get(id) ?? []) {
      const c = this.clusters.get(owner) ?? this.dormant.get(owner);
      if (c) result.push(...c.cells.values());
    }
    return result;
  }
  cellPosition(id: number): THREE.Vector3 | undefined {
    const owner = this.cellOwner.get(id);
    if (owner === undefined) return;
    const c = this.clusters.get(owner);
    if (c) {
      const cell = c.cells.get(id);
      if (cell) return this.toWorld(c, physicalCenter(cell));
    }
    const r = this.dormant.get(owner),
      cell = r?.cells.get(id);
    if (!r || !cell) return;
    const p = physicalCenter(cell);
    return new THREE.Vector3(p.x - r.origin.x, p.y - r.origin.y, p.z - r.origin.z)
      .applyQuaternion(new THREE.Quaternion(r.rotation.x, r.rotation.y, r.rotation.z, r.rotation.w))
      .add(new THREE.Vector3(r.position.x, r.position.y, r.position.z));
  }
  get buildings(): Building[] {
    return [...this.buildingIndex.values()];
  }
  /** Exact occupied aircraft cuboids sweep against active and streamed geometry. */
  flightColumnClear(point: Vec, height: number, excludeBuilding?: number, envelope?: Vec): boolean {
    const p = { x: point.x, y: point.y + (envelope?.y ?? 2), z: point.z },
      move = { x: 0, y: height, z: 0 },
      q = { x: 0, y: 0, z: 0, w: 1 },
      shape = envelope ? new RAPIER.Cuboid(envelope.x, envelope.y, envelope.z) : new RAPIER.Ball(0.35);
    const active = this.world.castShape(
      p,
      q,
      move,
      shape,
      0,
      1,
      !!envelope,
      undefined,
      undefined,
      undefined,
      undefined,
      (col) =>
        excludeBuilding === undefined ||
        this.clusters.get(this.colliderOwner.get(col.handle) ?? -1)?.building.id !== excludeBuilding,
    );
    const dormant = this.dormantNavigation.sweepShape(
      p,
      move,
      shape,
      q,
      envelope ?? { x: 0.35, y: 0.35, z: 0.35 },
    );
    return !active && !dormant;
  }
  sweepCluster(c: Cluster, move: THREE.Vector3): { time: number; point: THREE.Vector3 } | undefined {
    let best: { time: number; point: THREE.Vector3 } | undefined;
    const q = c.body.rotation(),
      rotation = new THREE.Matrix4().makeRotationFromQuaternion(
        new THREE.Quaternion(q.x, q.y, q.z, q.w),
      ).elements;
    for (const box of c.collisionBoxes) {
      const p = this.toWorld(c, {
          x: (box.x + box.w / 2) * CELL,
          y: (box.y + box.h / 2) * CELL,
          z: (box.z + box.d / 2) * CELL,
        }),
        half = { x: (box.w * CELL) / 2, y: (box.h * CELL) / 2, z: (box.d * CELL) / 2 },
        shape = new RAPIER.Cuboid(half.x, half.y, half.z);
      const extent = {
        x: Math.abs(rotation[0]) * half.x + Math.abs(rotation[4]) * half.y + Math.abs(rotation[8]) * half.z,
        y: Math.abs(rotation[1]) * half.x + Math.abs(rotation[5]) * half.y + Math.abs(rotation[9]) * half.z,
        z: Math.abs(rotation[2]) * half.x + Math.abs(rotation[6]) * half.y + Math.abs(rotation[10]) * half.z,
      };
      const active = this.world.castShape(
        p,
        q,
        move,
        shape,
        0,
        best?.time ?? 1,
        false,
        undefined,
        undefined,
        undefined,
        c.body,
        (collider) =>
          this.clusters.get(this.colliderOwner.get(collider.handle) ?? -1)?.building.id !== c.building.id,
      );
      const dormant = this.dormantNavigation.sweepShape(p, move, shape, q, extent);
      const time = Math.min(active?.time_of_impact ?? Infinity, dormant?.time ?? Infinity);
      if (time < (best?.time ?? 1))
        best = { time, point: new THREE.Vector3(p.x, p.y, p.z).addScaledVector(move, time) };
    }
    return best;
  }
  /** Finite craft query includes the same occupied geometry when streamed out. */
  occupiedBoxClear(
    point: Vec,
    half: Vec,
    rotation: THREE.Quaternion,
    move: Vec,
    exclude?: RAPIER.RigidBody,
  ): boolean {
    const shape = new RAPIER.Cuboid(half.x, half.y, half.z);
    const active = this.world.castShape(
      point,
      rotation,
      move,
      shape,
      0.03,
      1,
      true,
      undefined,
      undefined,
      undefined,
      exclude,
    );
    if (active) return false;
    const m = new THREE.Matrix4().makeRotationFromQuaternion(rotation).elements;
    const extent = {
      x: Math.abs(m[0]) * half.x + Math.abs(m[4]) * half.y + Math.abs(m[8]) * half.z,
      y: Math.abs(m[1]) * half.x + Math.abs(m[5]) * half.y + Math.abs(m[9]) * half.z,
      z: Math.abs(m[2]) * half.x + Math.abs(m[6]) * half.y + Math.abs(m[10]) * half.z,
    };
    return !this.dormantNavigation.sweepShape(point, move, shape, rotation, extent);
  }
  stream(focus: Vec, radius = 180): void {
    this.streamFocus = { x: focus.x, z: focus.z, radius };
    for (const r of this.dormant.values())
      if (
        !this.pendingActivate.has(r.id) &&
        !r.settled &&
        Math.hypot(r.position.x - focus.x, r.position.z - focus.z) < radius
      ) {
        const run = () => {
          this.pendingActivate.delete(r.id);
          const f = this.streamFocus;
          if (Math.hypot(r.position.x - f.x, r.position.z - f.z) < f.radius + 5) this.activate(r.id);
        };
        if (this.jobs.push(run)) this.pendingActivate.add(r.id);
      }
    let parked = 0;
    const moving: { owner?: number; position: Vec; radius: number }[] = [];
    this.world.forEachRigidBody((body) => {
      if (!body.isDynamic()) return;
      const first = body.numColliders() ? body.collider(0) : undefined;
      const cluster = first ? this.clusters.get(this.colliderOwner.get(first.handle) ?? -1) : undefined;
      const v = body.linvel();
      moving.push({
        owner: cluster?.id,
        position: body.translation(),
        radius: (cluster?.radius ?? 6) + Math.hypot(v.x, v.y, v.z) * 0.8 + 3,
      });
    });
    const started = performance.now();
    for (
      let scanned = 0;
      scanned < this.clusters.size + 1 && parked < 64 && performance.now() - started < 2;
      scanned++
    ) {
      this.parkingCursor ??= this.clusters.values();
      const c = this.parkingCursor.next().value;
      if (!c) {
        this.parkingCursor = undefined;
        break;
      }
      const p = c.body.translation();
      if (
        (!c.settled && Math.hypot(p.x - focus.x, p.z - focus.z) < radius + 40) ||
        (!c.anchored && !c.settled && (!c.body.isSleeping() || c.body.isKinematic())) ||
        this.stressed.has(c.id) ||
        this.damageInbox.hasOwner(c.id) ||
        (this.collisionPinnedUntil.get(c.id) ?? 0) > this.time ||
        moving.some(
          (m) =>
            m.owner !== c.id &&
            Math.hypot(m.position.x - p.x, m.position.y - p.y, m.position.z - p.z) < m.radius + c.radius,
        ) ||
        (this.pinnedCells.size > 0 && [...c.cells.keys()].some((id) => this.pinnedCells.has(id)))
      )
        continue;
      const r: DormantCluster = {
        id: c.id,
        revision: c.revision,
        building: c.building,
        cells: c.cells,
        anchored: c.anchored,
        origin: { x: c.origin.x, y: c.origin.y, z: c.origin.z },
        position: { ...p },
        rotation: { ...c.body.rotation() },
        settled: c.settled,
        supportOwner: c.supportOwner,
        supportPoint: c.supportPoint ? { ...c.supportPoint } : undefined,
      };
      // The existing baked vertices remain visible with the same detail. Only
      // a legacy sleeping dynamic batch needs conversion to a fixed page.
      if (!c.anchored && !c.settled) this.batches.set(c.id, c.mesh.geometry, c.mesh.matrix, true);
      this.remove(c, true);
      this.dormant.set(r.id, r);
      this.buildingOwners.get(r.building.id)?.add(r.id);
      this.dormantNavigation.add(r);
      this.renderedDormant.add(r.id);
      for (const cell of r.cells.values()) this.cellOwner.set(cell.id, r.id);
      this.streamedOut++;
      parked++;
    }
    this.world.propagateModifiedBodyPositionsToColliders();
  }
  ray(
    origin: Vec,
    direction: Vec,
    length = 900,
    exclude?: RAPIER.RigidBody,
    options?: { physical?: boolean; ignoreEntities?: boolean },
  ): Hit | null {
    const ray = new RAPIER.Ray(origin, direction);
    const cut = new Set<number>();
    if (this.cutaways.size && !options?.physical)
      for (const c of this.clusters.values())
        if (
          c.anchored &&
          (this.cutaways.has(c.building.id) ||
            (c.building.parentCell !== undefined &&
              this.cutaways.has(Math.floor(c.building.parentCell / 1000000))))
        )
          cut.add(c.id);
    const h = this.world.castRayAndGetNormal(
      ray,
      length,
      true,
      undefined,
      undefined,
      undefined,
      exclude,
      (col) => !cut.has(this.colliderOwner.get(col.handle) ?? -1),
    );
    let visibleHit: Hit | undefined,
      limit = h?.timeOfImpact ?? length;
    if (cut.size) {
      const cast = new THREE.Raycaster(
        new THREE.Vector3(origin.x, origin.y, origin.z),
        new THREE.Vector3(direction.x, direction.y, direction.z),
        0,
        limit,
      );
      for (const id of cut) {
        const c = this.clusters.get(id)!;
        if (!c.mesh.geometry.getAttribute('position')?.count) continue;
        c.mesh.updateMatrixWorld(true);
        const hits: THREE.Intersection[] = [];
        c.mesh.raycast(cast, hits);
        for (const hit of hits)
          if (hit.distance < limit) {
            limit = hit.distance;
            visibleHit = {
              point: hit.point,
              normal: (hit.face?.normal.clone() ?? new THREE.Vector3(0, 1, 0)).transformDirection(
                c.mesh.matrixWorld,
              ),
              cluster: c,
            };
          }
      }
    }
    const distant = this.dormantNavigation.ray(origin, direction, limit);
    if (distant && distant.time < limit) limit = distant.time;
    const entity = options?.ignoreEntities ? null : this.entityRay(origin, direction, limit);
    if (entity) return entity;
    if (distant && distant.time <= limit)
      return {
        point: new THREE.Vector3(origin.x, origin.y, origin.z).addScaledVector(
          new THREE.Vector3(direction.x, direction.y, direction.z),
          distant.time,
        ),
        normal: new THREE.Vector3(distant.normal.x, distant.normal.y, distant.normal.z),
        dormant: this.dormant.get(distant.owner),
      };
    if (visibleHit) return visibleHit;
    if (!h) return null;
    const body = h.collider.parent();
    return {
      point: new THREE.Vector3(
        origin.x + direction.x * h.timeOfImpact,
        origin.y + direction.y * h.timeOfImpact,
        origin.z + direction.z * h.timeOfImpact,
      ),
      normal: new THREE.Vector3(h.normal.x, h.normal.y, h.normal.z),
      cluster: this.clusters.get(this.colliderOwner.get(h.collider.handle) ?? -1),
      collider: h.collider,
      entity: body ? this.entityForBody(body) : undefined,
    };
  }
  blocked(x: number, z: number, radius = 0.42, y = 1.02, exclude?: RAPIER.RigidBody): boolean {
    return (
      !!this.world.intersectionWithShape(
        { x, y, z },
        { x: 0, y: 0, z: 0, w: 1 },
        new RAPIER.Cuboid(radius, 0.68, radius),
        undefined,
        undefined,
        undefined,
        exclude,
        (col) => this.colliderOwner.has(col.handle),
      ) || this.dormantNavigation.blocked({ x, y, z }, radius)
    );
  }
  safeSpot(wanted: Vec, exclude?: RAPIER.RigidBody): THREE.Vector3 {
    for (let ring = 0; ring < 25; ring++)
      for (let i = 0; i < Math.max(1, ring * 8); i++) {
        const angle = (i / Math.max(1, ring * 8)) * Math.PI * 2,
          x = THREE.MathUtils.clamp(wanted.x + Math.cos(angle) * ring * 0.85, -51, 51),
          z = THREE.MathUtils.clamp(wanted.z + Math.sin(angle) * ring * 0.85, -51, 51);
        if (!this.blocked(x, z, 0.52, 1.1, exclude)) return new THREE.Vector3(x, GROUND + 1.0, z);
      }
    return new THREE.Vector3(0, GROUND + 1, 49);
  }
  private invalidateParented(buildingId: number): void {
    for (const child of this.parentedByBuilding.get(buildingId) ?? []) this.pendingParented.add(child);
  }
  step(): void {
    const wholeStepStart = performance.now();
    let checked = 0;
    for (const id of this.pendingParented) {
      if (checked++ >= 16 || performance.now() - wholeStepStart > 0.7) break;
      this.pendingParented.delete(id);
      const building = this.buildingIndex.get(id);
      if (building?.parentCell === undefined) continue;
      const owner = this.cellOwner.get(building.parentCell);
      const support = owner === undefined ? undefined : (this.clusters.get(owner) ?? this.dormant.get(owner));
      if (support?.anchored) continue;
      for (const child of this.buildingOwners.get(id) ?? []) {
        const saved = this.clusters.get(child) ?? this.dormant.get(child);
        if (!saved?.anchored) continue;
        const c = this.clusters.get(child) ?? this.activate(child);
        if (!c) continue;
        c.anchored = false;
        this.structureVersion++;
        this.invalidateFoot(c);
        c.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
        for (const cell of c.cells.values()) {
          this.beforeCellChange(cell);
          cell.root = false;
        }
        const moving = owner === undefined ? undefined : this.clusters.get(owner);
        if (moving) c.body.setLinvel(moving.body.velocityAtPoint(c.body.translation()), true);
        this.rebuild(c);
      }
    }
    this.time += FIXED_DT;
    this.stressClock += FIXED_DT;
    this.streamClock += FIXED_DT;
    if (this.stressClock >= 0.15) {
      this.stressClock = 0;
      if (this.stressed.size && !this.stressQueued)
        this.stressQueued = this.jobs.push(() => {
          this.stressQueued = false;
          this.progressFailure();
        });
    }
    if (this.streamClock >= 0.35) {
      this.streamClock = 0;
      const candidates = new Map<number, boolean>();
      this.world.forEachActiveRigidBody((body) => {
        // Controlled planes/traffic use exact virtual sweeps themselves. This
        // path serves actual dynamic sections and external vehicle wrecks.
        if (!body.isDynamic()) return;
        const first = body.numColliders() ? body.collider(0) : undefined;
        const cluster = first ? this.clusters.get(this.colliderOwner.get(first.handle) ?? -1) : undefined;
        let radius = cluster?.radius ?? 1;
        if (!cluster)
          for (let i = 0; i < body.numColliders(); i++) {
            const shape = body.collider(i),
              h = shape.halfExtents(),
              local = shape.translationWrtParent();
            if (h)
              radius = Math.max(
                radius,
                Math.hypot(h.x, h.y, h.z) + (local ? Math.hypot(local.x, local.y, local.z) : 0),
              );
          }
        const p = body.translation(),
          v = body.linvel(),
          speed = Math.hypot(v.x, v.y, v.z);
        const start = new THREE.Vector3(p.x, p.y, p.z);
        const bounds = new THREE.Box3(start.clone(), start.clone())
          .expandByPoint(new THREE.Vector3(p.x + v.x * 1.3, p.y + v.y * 1.3 - 13.6, p.z + v.z * 1.3))
          .expandByScalar(radius + 22);
        this.dormantNavigation.queryBounds(bounds, (record, occupied) => {
          const urgent = occupied.distanceToPoint(start) < radius + speed * 0.36 + 8;
          candidates.set(record.id, urgent || (candidates.get(record.id) ?? false));
        });
      });
      for (const [id, urgent] of candidates) {
        if (urgent) {
          this.activate(id);
          this.collisionPinnedUntil.set(id, this.time + 0.8);
        } else if (!this.pendingPhysicsActivate.has(id)) {
          const run = () => {
            this.pendingPhysicsActivate.delete(id);
            if (this.activate(id)) this.collisionPinnedUntil.set(id, this.time + 0.8);
          };
          if (this.jobs.push(run)) this.pendingPhysicsActivate.add(id);
        }
      }
    }
    this.movingRubble = false;
    this.movingRubbleBounds.makeEmpty();
    for (const c of this.clusters.values())
      if (!c.anchored && !c.settled) {
        const p = c.body.translation(),
          q = c.body.rotation();
        c.previous.set(p.x, p.y, p.z);
        c.previousQ.set(q.x, q.y, q.z, q.w);
        const velocity = c.body.linvel();
        c.speedBeforeStep = Math.hypot(velocity.x, velocity.y, velocity.z);
        if (c.speedBeforeStep > 2.5) {
          this.movingRubble = true;
          // The full rotating cluster fits inside this origin-centred radius.
          // Include the coming fixed-step translation before rejecting any
          // distant pedestrian. Nearby actors still receive the Rapier test.
          const r = c.radius + c.speedBeforeStep * FIXED_DT + 0.05,
            b = this.movingRubbleBounds;
          b.min.x = Math.min(b.min.x, p.x - r);
          b.min.y = Math.min(b.min.y, p.y - r);
          b.min.z = Math.min(b.min.z, p.z - r);
          b.max.x = Math.max(b.max.x, p.x + r);
          b.max.y = Math.max(b.max.y, p.y + r);
          b.max.z = Math.max(b.max.z, p.z + r);
        }
        c.velocityBeforeStep.set(velocity.x, velocity.y, velocity.z);
        const angular = c.body.angvel();
        c.angularBeforeStep.set(angular.x, angular.y, angular.z);
      }
    const start = performance.now();
    this.world.step(this.events);
    this.physicsMs = performance.now() - start;
    let interactions = 0;
    const contactStart = performance.now();
    this.contactEvents = 0;
    const contacts = this.contactHandles;
    contacts.length = 0;
    this.events.drainContactForceEvents((e) => {
      this.contactEvents++;
      // Copy values while Rapier lends the event. Queries, game callbacks and
      // destruction run after the queue has released that borrow. The WASM
      // callback otherwise swallows a JS exception or obscures it on teardown.
      if (e.totalForceMagnitude() >= 900) contacts.push(e.collider1(), e.collider2());
    });
    const respond = (first: number, second: number): void => {
      const a = this.clusters.get(this.colliderOwner.get(first) ?? -1),
        b = this.clusters.get(this.colliderOwner.get(second) ?? -1);
      if (a?.id === b?.id) return;
      const moving = a && !a.anchored && !a.settled ? a : b,
        target = moving === a ? b : a;
      if (!moving || moving.anchored || moving.settled || this.time - moving.lastImpact < 0.65) return;
      const motionBound =
        moving.speedBeforeStep +
        moving.angularBeforeStep.length() * moving.radius +
        (target && !target.anchored
          ? target.speedBeforeStep + target.angularBeforeStep.length() * target.radius
          : 0);
      if (motionBound < 2.8) return;
      let point: Vec = moving.body.translation(),
        speed = 0;
      const ca = this.world.getCollider(first),
        cb = this.world.getCollider(second);
      if (!ca || !cb || !moving.body.isValid() || (target && !target.body.isValid())) return;
      const movingCom = moving.body.worldCom(),
        targetCom = target?.body.worldCom();
      const normalVelocity = (c: Cluster | undefined, com: Vec | undefined, p: Vec, n: Vec): number => {
        if (!c || c.anchored || c.settled || !com) return 0;
        const rx = p.x - com.x,
          ry = p.y - com.y,
          rz = p.z - com.z,
          w = c.angularBeforeStep,
          v = c.velocityBeforeStep;
        return (
          (v.x + w.y * rz - w.z * ry) * n.x +
          (v.y + w.z * rx - w.x * rz) * n.y +
          (v.z + w.x * ry - w.y * rx) * n.z
        );
      };
      // Compounds can contain several simultaneous contact planes. Select a
      // qualifying real impact instead of overwriting it with the last quiet
      // floor manifold. This changes routing, not the solver's impulse/energy.
      let contactError: unknown;
      this.world.contactPair(ca, cb, (m, flipped) => {
        try {
          const normal = m.normal();
          forContactPoints(m, ca, cb, flipped, (p, distance) => {
            if (distance > 0.04) return;
            const closing = Math.abs(
              normalVelocity(moving, movingCom, p, normal) - normalVelocity(target, targetCom, p, normal),
            );
            if (closing > speed) {
              speed = closing;
              point = { x: p.x, y: p.y, z: p.z };
            }
          });
        } catch (error) {
          contactError = error;
        }
      });
      if (contactError) throw contactError;
      if (speed < (IS_LITE ? 5 : 4)) return;
      moving.lastImpact = this.time;
      if (target && !target.anchored) target.lastImpact = this.time;
      interactions++;
      this.onRubble(Math.min(1, speed / 12), point);
      const p = { ...point };
      if (!target) {
        const other = (moving === a ? cb : ca).parent();
        if (other) this.onContact(other, p, speed * Math.min(8, moving.cells.size / 10));
      }
      if (moving.cells.size < WORKLOAD.secondaryMinimum) return;
      this.secondaryHits++;
      if (target)
        this.scheduleHit(
          target,
          p,
          Math.min(3.4, 1 + moving.cells.size / 70),
          Math.min(6, speed * 0.8),
          undefined,
          0,
        );
      // A severe landing can fracture a detached slab locally as well as a wall.
      this.scheduleHit(
        moving,
        p,
        Math.min(3, 1 + moving.cells.size / 90),
        Math.min(8, speed * 0.5),
        undefined,
        0,
      );
    };
    for (let i = 0; i < contacts.length && interactions < 3; i += 2) respond(contacts[i], contacts[i + 1]);
    contacts.length = 0;
    this.totalContactEvents += this.contactEvents;
    this.settleSupported();
    if (this.profileEnabled) {
      this.stepTimings.push({
        sequence: ++this.stepSequence,
        simulationSeconds: this.time,
        stepMs: this.physicsMs,
        totalStepMs: performance.now() - wholeStepStart,
        contactDrainMs: performance.now() - contactStart,
        contactForceEvents: this.contactEvents,
        broadPhaseMs: this.world.timingBroadPhase(),
        narrowPhaseMs: this.world.timingNarrowPhase(),
        solverMs: this.world.timingSolver(),
        ccdMs: this.world.timingCcd(),
        userChangesMs: this.world.timingUserChanges(),
        islandMs: this.world.timingIslandConstruction(),
      });
      if (this.stepTimings.length > 1200) this.stepTimings.shift();
    }
  }
  process(): void {
    const start = performance.now();
    this.structuralMs = 0;
    // A detonation stores the event-time transform, then evaluates primary
    // coverage in bounded slices. Topology cannot change those cells halfway
    // through capturing a simultaneous charge group.
    while (this.captureQueue.length && performance.now() - start < 2) {
      const task = this.captureQueue[0];
      const end = Math.min(task.cells.length, task.cursor + 64);
      if (this.life.valid(task.epoch))
        this.captureDamage(task.owner, task.cells.slice(task.cursor, end), task.local, task.request);
      this.captureCells -= end - task.cursor;
      task.cursor = end;
      if (end === task.cells.length) this.captureQueue.shift();
    }
    if (this.captureQueue.length) {
      this.structuralMs = performance.now() - start;
      return;
    }
    // One topology transaction at a time; fracture remaps any still-pending cells
    // directly into their new owner's bucket, avoiding a monolithic fan-out job.
    let owners = 0;
    while (this.damageInbox.ownerCount && owners < 8 && performance.now() - start < 5) {
      const id = this.damageInbox.nextOwner!;
      const c = this.activate(id),
        hit = this.damageInbox.take(id);
      if (c && hit) this.applyHits(c, [hit]);
      owners++;
    }
    this.processAssessments(Math.min(start + 5, performance.now() + 2.5));
    this.jobs.drain(8, Math.max(0, 5 - (performance.now() - start)));
    this.structuralMs = performance.now() - start;
  }
  get pendingWork(): number {
    return this.jobs.size + this.damageInbox.ownerCount + this.captureJobs + this.assessments.size;
  }
  private reactivate(c: Cluster): void {
    if (!c.settled) return;
    c.settled = false;
    this.structureVersion++;
    this.invalidateFoot(c);
    c.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
    for (const observe of this.supportObservers) observe(c.building.id);
    for (const shape of c.colliders) shape.setCollisionGroups(0x0002ffff);
    c.colliderGroups = 0x0002ffff;
    c.body.wakeUp();
    c.quietSince = this.time;
    c.lastImpact = this.time;
    this.dynamicStructuralColliders += c.colliders.length;
    this.dynamicStructuralCuboids += c.collisionBoxes.length;
    if (c.supportOwner !== undefined) this.dependents.get(c.supportOwner)?.delete(c.id);
    c.supportOwner = undefined;
    c.supportPoint = undefined;
    this.reactivations++;
    this.batches.set(c.id, c.mesh.geometry, c.mesh.matrix);
    this.reactivateDependents(c.id);
  }
  private reactivateDependents(id: number, changed?: Cluster): void {
    const dependents = this.dependents.get(id);
    this.dependents.delete(id);
    for (const child of dependents ?? []) {
      const c = this.clusters.get(child) ?? this.activate(child);
      if (c?.supportPoint && changed?.anchored) {
        const point = this.toLocal(changed, c.supportPoint);
        let stillSupported = false;
        for (let dx = -1; dx <= 1 && !stillSupported; dx++)
          for (let dy = -1; dy <= 1 && !stillSupported; dy++)
            for (let dz = -1; dz <= 1 && !stillSupported; dz++) {
              const cell = changed.cells.get(
                changed.building.id * 1000000 +
                  key(
                    Math.floor(point.x / CELL) + dx,
                    Math.floor(point.y / CELL) + dy,
                    Math.floor(point.z / CELL) + dz,
                  ),
              );
              if (!cell) continue;
              const b = cell.bounds,
                center = physicalCenter(cell);
              stillSupported =
                Math.abs(point.x - center.x) < (CELL * (b?.w ?? 1)) / 2 + 0.035 &&
                Math.abs(point.z - center.z) < (CELL * (b?.d ?? 1)) / 2 + 0.035 &&
                Math.abs(point.y - (center.y + (CELL * (b?.h ?? 1)) / 2)) < 0.09;
            }
        if (stillSupported) {
          let saved = this.dependents.get(id);
          if (!saved) this.dependents.set(id, (saved = new Set()));
          saved.add(child);
          continue;
        }
      }
      if (c) this.reactivate(c);
    }
  }
  /** Only a quiet body with an actual upward load-bearing contact to a rooted
   * fixed body can become a settled proxy. Independent airborne islands are
   * never coupled. The support graph reactivates dependents when support changes.
   */
  private settleSupported(): void {
    const start = performance.now();
    let checked = 0;
    for (let scanned = 0; scanned < 48 && checked < 6 && performance.now() - start < 0.8; scanned++) {
      this.settlementCursor ??= this.clusters.values();
      const c = this.settlementCursor.next().value;
      if (!c) {
        this.settlementCursor = undefined;
        break;
      }
      if (c.anchored || c.settled || c.body.isKinematic() || this.damageInbox.hasOwner(c.id)) continue;
      const v = c.body.linvel(),
        a = c.body.angvel();
      if (Math.hypot(v.x, v.y, v.z) > 0.08 || Math.hypot(a.x, a.y, a.z) * c.radius > 0.1) {
        c.quietSince = this.time;
        continue;
      }
      if (!c.body.isSleeping() && this.time - c.quietSince < 1.2) continue;
      checked++;
      const comY = c.body.worldCom().y;
      let support: number | undefined;
      for (const shape of c.colliders) {
        this.world.contactPairsWith(shape, (other) => {
          if (support !== undefined || !other.parent()?.isFixed()) return;
          this.world.contactPair(shape, other, (m, flipped) => {
            if (Math.abs(m.normal().y) < 0.65) return;
            forContactPoints(m, shape, other, flipped, (p, distance) => {
              if (support === undefined && p.y < comY + 0.03 && distance < 0.04) {
                support = this.colliderOwner.get(other.handle) ?? -1;
                c.supportPoint = { ...p };
              }
            });
          });
        });
        if (support !== undefined) break;
      }
      if (support === undefined) continue;
      c.settled = true;
      this.structureVersion++;
      this.invalidateFoot(c);
      c.supportOwner = support;
      c.body.setBodyType(RAPIER.RigidBodyType.Fixed, false);
      for (const observe of this.supportObservers) observe(c.building.id);
      // Explicitly remove settled-to-settled pairs from the narrow phase. Merely
      // changing body type leaves a large contact cache after a dense collapse.
      const replacements: RAPIER.Collider[] = [];
      for (const shape of c.colliders) {
        const p = shape.translationWrtParent()!,
          q = shape.rotationWrtParent()!;
        const desc = new RAPIER.ColliderDesc(shape.shape)
          .setTranslation(p.x, p.y, p.z)
          .setRotation(q)
          .setCollisionGroups(0x0008fff7)
          .setFriction(0.82)
          .setRestitution(0.025)
          .setDensity(0)
          .setActiveEvents(RAPIER.ActiveEvents.CONTACT_FORCE_EVENTS)
          .setContactForceEventThreshold(900);
        this.colliderOwner.delete(shape.handle);
        this.world.removeCollider(shape, false);
        const replacement = this.world.createCollider(desc, c.body);
        replacements.push(replacement);
        this.colliderOwner.set(replacement.handle, c.id);
      }
      c.colliders = replacements;
      c.colliderGroups = 0x0008fff7;
      c.velocityBeforeStep.set(0, 0, 0);
      c.angularBeforeStep.set(0, 0, 0);
      c.speedBeforeStep = 0;
      this.dynamicStructuralColliders -= c.colliders.length;
      this.dynamicStructuralCuboids -= c.collisionBoxes.length;
      this.settledTransitions++;
      let children = this.dependents.get(support);
      if (!children) this.dependents.set(support, (children = new Set()));
      children.add(c.id);
      this.syncOne(c, 1);
      this.batches.set(c.id, c.mesh.geometry, c.mesh.matrix, true);
    }
  }
  sync(alpha: number): void {
    // Phased restoration writes real partial geometry into these same pages,
    // including dormant outer districts. There is no camera-local fake mesh.
    const batching = true;
    this.batches.root.visible = batching;
    for (const c of this.clusters.values()) {
      c.mesh.visible = !batching;
      if (batching && c.mesh.parent) this.scene.remove(c.mesh);
      else if (!batching && !c.mesh.parent) this.scene.add(c.mesh);
      if (!c.anchored && !c.settled) {
        const sleeping = c.body.isSleeping();
        if (!sleeping || !c.renderSleeping) this.syncOne(c, sleeping ? 1 : alpha);
        c.renderSleeping = sleeping;
      }
    }
  }
  updateDetail(camera: THREE.Vector3): void {
    const list = [...this.clusters.values()];
    if (!list.length) return;
    const c = list[this.detailCursor++ % list.length],
      p = c.body.translation();
    const distance = camera.distanceTo(new THREE.Vector3(p.x, p.y, p.z));
    const desired = c.detail ? distance < this.detailDistance + 15 : distance < this.detailDistance - 15;
    if (desired !== c.detail) {
      c.detail = desired;
      c.mesh.geometry.dispose();
      c.geometryCache?.clear();
      c.geometryCache = undefined;
      c.mesh.geometry = buildingGeometry(
        c.building,
        this.visibleCells(c),
        c.origin,
        desired,
        undefined,
        this.glassBand(c),
      );
      c.mesh.updateMatrix();
      this.batches.set(c.id, c.mesh.geometry, c.mesh.matrix, c.anchored || c.settled);
    }
  }
  private syncOne(c: Cluster, alpha: number): void {
    const p = c.body.translation(),
      q = c.body.rotation();
    c.mesh.position.copy(c.previous).lerp(new THREE.Vector3(p.x, p.y, p.z), alpha);
    c.mesh.quaternion.copy(c.previousQ).slerp(new THREE.Quaternion(q.x, q.y, q.z, q.w), alpha);
    c.mesh.updateMatrix();
    this.batches.transform(c.id, c.mesh.matrix);
  }
  get dynamicCount(): number {
    let n = 0;
    this.world.forEachRigidBody((b) => {
      if (b.isDynamic()) n++;
    });
    return n;
  }
  get awake(): number {
    let n = 0;
    this.world.forEachRigidBody((b) => {
      if (b.isDynamic() && !b.isSleeping()) n++;
    });
    return n;
  }
  get bodyCounts(): Record<string, number> {
    const counts = {
      dynamicBodies: 0,
      sleepingBodies: 0,
      fixedBodies: 0,
      kinematicBodies: 0,
      dynamicStructuralProxies: this.dynamicStructuralColliders,
      dynamicStructuralCuboids: this.dynamicStructuralCuboids,
      footProofReused: this.footClearance.reused,
      footProofBuilt: this.footClearance.built,
      footProofFallback: this.footClearance.fallback,
      footProofSupportReused: this.footClearance.supportReused,
      footDynamicVolumes: this.dynamicFootQueries.queries,
      footDynamicReused: this.dynamicFootQueries.reused,
      footDynamicSkipped: this.dynamicFootQueries.skipped,
      footDynamicCasts: this.dynamicFootQueries.casts,
      footFineVolumes: this.dynamicFootQueries.fineQueries,
      footFineSkipped: this.dynamicFootQueries.fineSkipped,
      reconstructionQueryCommits: this.reconstructionQueryCommits,
      reconstructionQuerySkips: this.reconstructionQuerySkips,
      footObstructionReused: this.footClearance.obstructionReused,
      dormantGroundCalls: this.dormantNavigation.groundCalls,
      dormantGroundCandidates: this.dormantNavigation.groundCandidates,
      dormantShapeIndexBuilds: this.dormantNavigation.shapeIndexBuilds,
      dormantShapeIndexQueries: this.dormantNavigation.shapeIndexQueries,
    };
    this.world.forEachRigidBody((b) => {
      if (b.isDynamic()) {
        counts.dynamicBodies++;
        if (b.isSleeping()) counts.sleepingBodies++;
      } else if (b.isFixed()) counts.fixedBodies++;
      else counts.kinematicBodies++;
    });
    return counts;
  }
  contactAudit(): Record<string, number> {
    const start = performance.now();
    const counts: Record<string, number> = {
      candidatePairs: 0,
      touchingPairs: 0,
      solverContacts: 0,
      geometricContacts: 0,
      compoundManifolds: 0,
      fixedFixedPairs: 0,
      dynamicPairs: 0,
    };
    this.world.forEachCollider((a) => {
      this.world.contactPairsWith(a, (b) => {
        if (a.handle >= b.handle) return;
        counts.candidatePairs++;
        let contacts = 0;
        this.world.contactPair(a, b, (m) => {
          contacts += m.numContacts();
          counts.solverContacts += m.numSolverContacts();
          counts.geometricContacts += m.numContacts();
          if (a.shape instanceof RAPIER.Compound || b.shape instanceof RAPIER.Compound)
            counts.compoundManifolds++;
        });
        if (!contacts) return;
        counts.touchingPairs++;
        if (!a.parent()?.isDynamic() && !b.parent()?.isDynamic()) counts.fixedFixedPairs++;
        else counts.dynamicPairs++;
      });
    });
    counts.auditMs = performance.now() - start;
    return counts;
  }
  get liveCells(): number {
    let n = 0;
    for (const c of this.clusters.values()) n += c.cells.size;
    for (const c of this.dormant.values()) n += c.cells.size;
    return n;
  }
  get materialCount(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const name of Object.keys(MATERIALS)) counts[name] = 0;
    for (const c of this.clusters.values()) for (const cell of c.cells.values()) counts[cell.material]++;
    return counts;
  }
}
